import type { SxProps, Theme } from "@mui/material";
import type { FC } from "react";
import { FEATURE_FLAGS } from "@/contexts/FeatureFlagsProvider";
import { useJobs } from "@/hooks/useJobs";
import { useFeatureFlag } from "@/hooks/useOpenFeatureFlag";
import type { NurseRole } from "@/utils/models";
import { FilterActiveDot } from "../Badges/FilterActiveDot";
import { JobSelector } from "./JobSelector";
import { RoleChips } from "./RoleChips";

type Props = {
	// Role mode
	selectedRoles: (NurseRole | "all")[];
	onSelectedRolesChanged: (selected: (NurseRole | "all")[]) => void;
	minimumOneSelected?: boolean;
	availableRoles?: NurseRole[];
	// Job mode
	selectedJobIds: string[];
	onSelectedJobsChanged: (selected: string[]) => void;
	/** Restrict the job dropdown to these job ids (e.g. only jobs that map to a coverage role-group).
	    Omit to offer every enabled job. Ignored in role-chip mode. */
	availableJobIds?: string[];
	/**
	 * Pass to put a `FilterActiveDot` on the job dropdown: true while the job filter hides something. The caller decides,
	 * since whether an empty selection means "none" or "all" is the caller's convention. Role chips show their selection already.
	 */
	jobFilterActive?: boolean;
	// Job dropdown styling
	label?: string;
	formControlSx?: SxProps<Theme>;
	selectSx?: SxProps<Theme>;
};

/**
 * Picks role chips or a multi-select job dropdown based on the JobManagementEnabled flag.
 * Consumers maintain both selection states so toggling the flag preserves the inactive one.
 */
export const RoleOrJobSelector: FC<Props> = ({
	selectedRoles,
	onSelectedRolesChanged,
	minimumOneSelected,
	availableRoles,
	selectedJobIds,
	onSelectedJobsChanged,
	availableJobIds,
	jobFilterActive,
	label,
	formControlSx,
	selectSx,
}) => {
	const jobManagementEnabled = useFeatureFlag(FEATURE_FLAGS.JobManagementEnabled);
	const jobs = useJobs();
	const shownJobs = availableJobIds ? jobs.filter((j) => availableJobIds.includes(j.id)) : jobs;

	if (jobManagementEnabled && shownJobs.length > 0) {
		// JobManagement on but no jobs configured: fall back to role chips so consumers don't render an empty dropdown.
		const jobSelector = (
			<JobSelector
				variant="dropdown"
				jobs={shownJobs}
				selectedJobIds={selectedJobIds}
				onSelectedJobsChanged={onSelectedJobsChanged}
				label={label}
				formControlSx={formControlSx}
				selectSx={selectSx}
			/>
		);
		if (jobFilterActive === undefined) return jobSelector;
		return (
			<FilterActiveDot active={jobFilterActive} dataTestId="job-filter-active-dot">
				{jobSelector}
			</FilterActiveDot>
		);
	}

	return (
		<RoleChips
			onSelectedRoleFiltersChanged={onSelectedRolesChanged}
			initiallySelected={selectedRoles}
			minimumOneSelected={minimumOneSelected}
			availableRoles={availableRoles}
		/>
	);
};
